import type { SUPPORTED_LANGUAGES_TYPE } from 'i18n';
import { SUPPORTED_NAMESPACES } from 'i18n';
import type { TOptionsBase } from 'i18next';
import { useMemo } from 'react';
import type { UseTranslationOptions } from 'react-i18next';
import { getI18n, useTranslation } from 'react-i18next';

const LOCALSTORAGE_LANGUAGE_CODE_KEY = 'languageCode';

export const saveLanguageCode = (code: SUPPORTED_LANGUAGES_TYPE): void => {
  localStorage.setItem(LOCALSTORAGE_LANGUAGE_CODE_KEY, code);
};

export const getLanguageCode = (): SUPPORTED_LANGUAGES_TYPE | undefined => {
  return (
    (localStorage.getItem(LOCALSTORAGE_LANGUAGE_CODE_KEY) as SUPPORTED_LANGUAGES_TYPE) ?? undefined
  );
};

export const getI18nTranslate = (key: string, options?: any): string => {
  const i18n = getI18n();

  return i18n.t(key, options);
};

/** Expose every translator from one hook; readiness follows the requested namespaces (all by default).
 * The legacy `t` remains bound to common_old.
 */
export const useCustomTranslation = (
  options?: Pick<UseTranslationOptions, 'i18n' | 'useSuspense' | 'bindI18n'> & {
    namespaces?: string[];
  }
) => {
  const { namespaces = SUPPORTED_NAMESPACES, ...translationOptions } = options ?? {};
  const { t, i18n, ready } = useTranslation(namespaces, {
    ...translationOptions,
    nsMode: 'default'
  });

  // Recreate translators when the source t changes (including language changes),
  // while keeping their identity stable across unrelated renders.
  const translators = useMemo(() => {
    const forNamespace =
      (namespace: string) =>
      (
        key: string,
        translationOptions?: TOptionsBase & Record<string, number | string | undefined>
      ) =>
        t(key, { ns: namespace, ...translationOptions });

    return {
      tInput: forNamespace('inputs'),
      tError: forNamespace('errors'),
      tCommon: forNamespace('common'),
      tOrder: forNamespace('order'),
      tServiceObject: forNamespace('serviceObject'),
      tIntervention: forNamespace('intervention'),
      tRequest: forNamespace('request'),
      tCompany: forNamespace('company'),
      tCustomForm: forNamespace('customForm'),
      tReport: forNamespace('reports'),
      tCustomer: forNamespace('customer'),
      tPermission: forNamespace('permissions'),
      tMaterial: forNamespace('material'),
      tSetting: forNamespace('settings'),
      tSupportTicket: forNamespace('supportTicket'),
      tSubscription: forNamespace('subscriptions'),
      tAnnouncement: forNamespace('announcements'),
      tAudit: forNamespace('audit'),
      tDashboard: forNamespace('dashboard')
    };
  }, [t]);

  // Expose ready so callers using useSuspense: false can render their own loading state
  // until the requested namespaces are loaded. With Suspense enabled, React handles waiting.
  // This indicates readiness of the requested namespaces, not whether an individual key exists.
  return { t, i18n, ready, ...translators };
};
